import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import Table from '../../../../core/common/dataTable/index';
//import TooltipOption from '../../../../core/common/tooltipOption';
import ErrorModal from '../../../../core/common/ErrorModal';
import SuccessModal from '../../../../core/common/successModal';
import { getUserDetails } from '../../../../lib/getUserDetails';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../core/data/redux/store';
import { fetchSchoolApi, SchoolResponse, deleteSchoolApi } from '../../../../core/data/services/schoolService';
import ConfirmationModal from '../../../../core/common/ConfirmationModal';
import { setSchoolList } from '../../../../core/data/redux/schoolSlice';
import SchoolFilter from './components/SchoolFilter';
import { School } from '../../../../core/data/types/school';
import { getCurrentUserEntity, getCurrentUserRoleType } from '../../../../core/data/services/adminService';
import CommonSelect,{Option} from '../../../../core/common/commonSelect';

// Define the filter interface
interface SchoolFilters {
  name: string;
  address: string;
  phone: string;
  schoolCode: string;
  stateId: number | null;
  districtId: number | null;
  blockAreaId: number | null;
  villageWardId: number | null;
  search?: string;
}

// Initial filter state
const initialFilters: SchoolFilters = {
  name: '',
  address: '',
  phone: '',
  schoolCode: '',
  stateId: null,
  districtId: null,
  blockAreaId: null,
  villageWardId: null,
  search: '',
};

const SchoolGrid = () => {
  const routes = all_routes;
  //const [viewType] = useState<'grid' | 'list'>('list');
  const [filters, setFilters] = useState<SchoolFilters>(initialFilters);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [error, setError] = useState<string>('');
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(false);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const userEntityData = getCurrentUserEntity();

  const roleType = getCurrentUserRoleType();
  console.log('roleType', roleType);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  // Sorting state
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchValue, setSearchValue] = useState<string>('');

  // Server search last value ref
  const lastSearchValueRef = useRef<string>('');

  // Get schools from Redux store
  const schools = useSelector((state: RootState) => state.school.schoolList);
  const dispatch = useDispatch();

  // Get user details and blockAreaId
  const userData = getUserDetails();
  const blockAreaId = userData?.[0]?.relatedEntity?.villageWards?.blockAreaId;

  // Fetch schools function with robust error handling
  const fetchSchools = async (page: number = 1, size: number = 10, filterParams?: SchoolFilters, sort?: { sortBy?: string; sortOrder?: 'asc' | 'desc' }) => {
    let newFilterParams: any = filterParams;
    // if (!blockAreaId) {
    //   console.warn('blockAreaId is not available');
    //   setError('User location information is not available');
    //   setShowErrorModal(true);
    //   return;
    // }

    if (roleType === 'state') {
      newFilterParams.stateId = userEntityData?.states?.id ?? null;
    } else if (roleType === 'district') {
      newFilterParams.stateId = userEntityData?.districts?.stateId ?? null;
      newFilterParams.districtId = userEntityData?.districts?.id ?? null;
    } else
      if (roleType === 'block') {
        newFilterParams.stateId = userEntityData?.blockAreas?.stateId ?? null;
        newFilterParams.districtId = userEntityData?.blockAreas?.districtId ?? null;
        newFilterParams.blockAreaId = userEntityData?.blockAreas?.id ?? null;
      }
      else if (roleType === 'villageWard') {
        newFilterParams.stateId = userEntityData?.villageWards?.stateId ?? null;
        newFilterParams.districtId = userEntityData?.villageWards?.districtId ?? null;
        newFilterParams.blockAreaId = userEntityData?.villageWards?.blockAreaId ?? null;
        newFilterParams.villageWardId = userEntityData?.villageWards?.id ?? null;
      }
    setIsLoading(true);
    try {
      const params = {
        blockAreaId,
        page,
        pageSize: size,
        sortBy: sort?.sortBy || sortBy || 'name',
        order: sort?.sortOrder || sortOrder || 'asc',
        search: (newFilterParams?.search && newFilterParams.search.trim() !== '') ? newFilterParams.search : (undefined),
        ...(newFilterParams && {
          name: (newFilterParams.name || undefined),
          address: newFilterParams.address || undefined,
          phone: newFilterParams.phone || undefined,
          schoolCode: newFilterParams.schoolCode || undefined,
          stateId: newFilterParams.stateId ? Number(newFilterParams.stateId) : undefined,
          districtId: newFilterParams.districtId ? Number(newFilterParams.districtId) : undefined,
          blockAreaId: newFilterParams.blockAreaId ? Number(newFilterParams.blockAreaId) : undefined,
          villageWardId: newFilterParams.villageWardId ? Number(newFilterParams.villageWardId) : undefined,
        })
      };

      console.log('Fetching schools with params:', params);
      const response: SchoolResponse = await fetchSchoolApi(params);
      console.log('Schools response:', response);

      // Handle different response structures
      const schoolsData = response?.data || response || [];
      const totalCount = response?.total || schoolsData?.length || 0;
      const currentPageNum = response?.page || page;
      const currentPageSize = response?.pageSize || size;
      const totalPagesCount = response?.totalPages || Math.ceil(totalCount / currentPageSize);

      console.log('Processed data:', {
        schoolsData,
        totalCount,
        currentPageNum,
        currentPageSize,
        totalPagesCount
      });

      dispatch(setSchoolList(Array.isArray(schoolsData) ? schoolsData : []));
      setTotal(totalCount);
      setTotalPages(totalPagesCount);
      setCurrentPage(currentPageNum);
      setPageSize(currentPageSize);

    } catch (error: any) {
      console.error('Error fetching schools:', error);
      setError(error.message || 'Failed to fetch schools');
      setShowErrorModal(true);
      dispatch(setSchoolList([]));
      setTotal(0);
      setTotalPages(0);
      setCurrentPage(1);
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load - fetch first 10 records
  useEffect(() => {
    fetchSchools(1, 10, filters, { sortBy, sortOrder });
  }, []);

  // Apply filters function
  const applyFilters = async () => {
    setIsFilterLoading(true);
    try {
      await fetchSchools(1, pageSize, filters, { sortBy, sortOrder });
      setCurrentPage(1);
    } catch (error: any) {
      setError(error.message || 'Failed to apply filters');
      setShowErrorModal(true);
    } finally {
      setIsFilterLoading(false);
    }
  };

  // Reset filters function
  const resetFilters = async () => {
    setFilters(initialFilters);
    await fetchSchools(1, pageSize, undefined, { sortBy: 'name', sortOrder: 'asc' });
    setSortBy('name');
    setSortOrder('asc');
    setCurrentPage(1);
  };

  // Handle pagination change
  const pageSizeOptions: Option[] = [
  { value: "10", label: "10" },
  // { value: "25", label: "25" },
  // { value: "50", label: "50" },
];

const currentPageSize: Option | null =
  pageSize != null
    ? pageSizeOptions.find(o => o.value === String(pageSize)) ?? null
    : null;

  const handlePageChange = async (page: number, size: number) => {
    console.log('Page change:', page, size);
    if (page < 1 || page > totalPages) {
      console.warn('Invalid page number:', page);
      return;
    }
    await fetchSchools(page, size, filters, { sortBy, sortOrder });
  };

  // Handle table sort change
  const handleTableChange = async (_pagination: any, _filters: any, sorter: any) => {
    const extractSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const newSortBy = extractSorter?.field || extractSorter?.columnKey || sortBy;
    const newSortOrder = extractSorter?.order === 'ascend' ? 'asc' : extractSorter?.order === 'descend' ? 'desc' : undefined;

    if (!extractSorter?.order) {
      // Clear sort: reset to defaults or no sort
      setSortBy('name');
      setSortOrder('asc');
      await fetchSchools(1, pageSize, filters, { sortBy: 'name', sortOrder: 'asc' });
      setCurrentPage(1);
      return;
    }

    if (newSortBy && newSortOrder) {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
      await fetchSchools(1, pageSize, filters, { sortBy: newSortBy, sortOrder: newSortOrder });
      setCurrentPage(1);
    }
  };

  // Stable server-side search handler
  const handleServerSearch = React.useCallback((value: string) => {
    if (value === lastSearchValueRef.current) return;
    lastSearchValueRef.current = value;
    setSearchValue(value);
    const newFilters = { ...filters, search: value, name: value };
    setFilters(newFilters);
    fetchSchools(1, pageSize, newFilters, { sortBy, sortOrder });
    setCurrentPage(1);
  }, [filters, pageSize, sortBy, sortOrder]);

  // Delete modal state
  const [schoolToDelete, setSchoolToDelete] = useState<School | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  // Open delete modal
  const requestDeleteSchool = (school: School) => {
    setSchoolToDelete(school);
    setShowDeleteModal(true);
  };

  // Confirm delete from modal
  const handleConfirmDelete = async () => {
    if (!schoolToDelete) return;
    try {
      setIsLoading(true);
      await deleteSchoolApi(Number(schoolToDelete.id));
      setSuccessMessage('School deleted successfully');
      setShowSuccessModal(true);
      await fetchSchools(currentPage, pageSize, filters, { sortBy, sortOrder });
    } catch (err: any) {
      setError(err.message || 'Failed to delete school');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
      setSchoolToDelete(null);
      setShowDeleteModal(false);
    }
  };

  const handleCancelDelete = () => {
    setSchoolToDelete(null);
    setShowDeleteModal(false);
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== '');
  };

  // Get count of active filters
  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  // Handle apply filter click
  const handleApplyClick = () => {
    applyFilters();
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
  };

  // Calculate pagination display values with safety checks
  const getPaginationDisplay = () => {
    if (!total || total === 0) {
      return { start: 0, end: 0 };
    }
    const start = ((currentPage - 1) * pageSize) + 1;
    const end = Math.min(currentPage * pageSize, total);
    return { start, end };
  };

  const { start, end } = getPaginationDisplay();

  // Table columns (controlled sort indicators)
  const columns = useMemo(() => [
    {
      title: "School Name",
      dataIndex: "name",
      key: 'name',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortBy === 'name' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: "School Code",
      dataIndex: "schoolCode",
      key: 'schoolCode',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortBy === 'schoolCode' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: "UDISE Code",
      dataIndex: "udiseCode",
      key: 'udiseCode',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortBy === 'udiseCode' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: 'address',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortBy === 'address' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: 'phone',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: sortBy === 'phone' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
    },
    {
      title: "Shift",
      dataIndex: "shift",
      key: 'shift',
      render: (shift: string) => shift ? shift.charAt(0).toUpperCase() + shift.slice(1) : '',
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: 'gender',
      render: (gender: string) => {
        if (!gender) return '';
        switch (gender) {
          case 'boys':
            return 'Boys';
          case 'girls':
            return 'Girls';
          case 'co-ed':
            return 'Co-ed';
          default:
            return gender;
        }
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      key: 'action',
      render: (_: any, record: School) => (
        <div className="d-flex align-items-center">
          <div className="dropdown">
            <Link
              to="#"
              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="ti ti-dots-vertical fs-14" />
            </Link>
            <ul className="dropdown-menu dropdown-menu-right p-3">
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to={routes.schoolDetails}
                  state={{ school: record }}
                >
                  <i className="ti ti-eye me-2" />
                  View Details
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item rounded-1"
                  to={routes.editSchool}
                  state={{ school: record }}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li>
              <li>
                <Link
                  to="#"
                  className="dropdown-item rounded-1 text-danger"
                  onClick={(e) => { e.preventDefault(); requestDeleteSchool(record); }}
                >
                  <i className="ti ti-trash me-2" />
                  Delete
                </Link>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ], [routes, sortBy, sortOrder]);

   const user = useSelector((state: RootState) => state.user.Users);
    const userdetail = useSelector((state: RootState) => state.user.Userdetail);
  
   
    const currentUser = user.length > 0 ? user[0] : null;
    const roleName = currentUser?.role || userdetail?.designation || 'No role';
   
  
  
  
    // Role -> dashboard route mapping
    const getDashboardRoute = (roleRaw: string) => {
      const role = roleRaw.toLowerCase().trim();
  
      const direct: Record<string, keyof typeof all_routes> = {
        teacher: "teacherDashboard",
        hm: "HeadmasterDashboard",
        admin: "adminDashboard",
        "state admin": "adminDashboard",
        "district admin": "adminDashboard",
        "block admin": "adminDashboard",
        "village admin": "adminDashboard"
      };
  
      if (role in direct) return all_routes[direct[role]];
  
      if (role.includes("teacher")) return all_routes.teacherDashboard;
      if (role.includes("head") || role.includes("hm")) return all_routes.HeadmasterDashboard;
      if (role.includes("admin")) return all_routes.adminDashboard;
  
      const fallback: keyof typeof all_routes = "adminDashboard";
      return all_routes[fallback] ?? "/";
    };
  
  
    const dashboardLink = useMemo(() => getDashboardRoute(roleName), [roleName]);
  

  return (
    <div>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Schools List</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={dashboardLink}>Dashboard</Link>
                    
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    All Schools
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {/* <TooltipOption /> */}
              <div className="mb-2">
                <Link
                  to={routes.addSchool}
                  className="btn btn-primary"
                >
                  <i className="ti ti-square-rounded-plus-filled me-2" />
                  Add School
                </Link>
              </div>
            </div>
          </div>
          {/* /Page Header */}

          {/* Schools List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Schools</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                    {hasActiveFilters() && (
                      <span className="badge badge-primary ms-1">
                        {getActiveFilterCount()}
                      </span>
                    )}
                    {isFilterLoading && (
                      <span className="spinner-border spinner-border-sm ms-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </span>
                    )}
                  </Link>
                  <SchoolFilter
                    filters={filters}
                    onFilterChange={setFilters}
                    onApply={handleApplyClick}
                    onReset={resetFilters}
                    isLoading={isFilterLoading}
                  />
                </div>
              </div>
            </div>
            <div className="card-body p-0 py-3">
              {isLoading ? (
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading schools...</p>
                </div>
              ) : (
                <>
                  <Table
                    pagination={false}
                    columns={columns}
                    dataSource={schools}
                    Selection={false}
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                    onChange={handleTableChange}
                    serverSideSearch={true}
                    onSearch={handleServerSearch}
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                  />

                  {/* Pagination Info */}
                  <div className="d-flex justify-content-between align-items-center p-3 border-top">
                    <div className="text-muted">
                      Showing {start} to {end} of {total} schools
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="me-3">Rows per page:</span>
                      <CommonSelect
                        styles={{
                          control: (base: any) => ({ ...base, minHeight: 30, height: 30 }),
                          indicatorsContainer: (b: any) => ({ ...b, height: 30 }),
                          valueContainer: (b: any) => ({ ...b, padding: "0 8px" }),
                        }}
                        options={pageSizeOptions}
                        defaultValue={currentPageSize}
                        isDisabled={pageSizeOptions.length === 0}
                        onChange={(opt) => {
                          const size = opt?.value ? Number(opt.value) : pageSize;
                          handlePageChange(1, size);
                        }}
                      />

                      <nav>
                        <ul className="pagination pagination-sm mb-0">
                          <li className={`page-item ${ currentPage === 1 ? 'disabled' : '' }`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1, pageSize)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>

                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                            if (pageNum > totalPages) return null;

                            return (
                              <li key={pageNum} className={`page-item ${ currentPage === pageNum ? 'active' : '' }`}>
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(pageNum, pageSize)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            );
                          })}

                          <li className={`page-item ${ currentPage === totalPages ? 'disabled' : '' }`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1, pageSize)}
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {/* /Schools List */}
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />

      {/* Error Modal */}
      <ErrorModal
        show={showErrorModal}
        message={error}
        onClose={() => setShowErrorModal(false)}
      />

      {/* Confirm Delete Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        text={`Are you sure you want to delete "${ schoolToDelete?.name ?? '' }"? This action cannot be undone.`}
        onConfirmDelete={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default SchoolGrid;
